import { Injectable } from '@angular/core';
import { GetManyV2Dto, PageableListV2Dto } from '@fleet-beat/core';
import moment from 'moment';
import { delay, Observable, of, throwError } from 'rxjs';

import { AssistanceTypeEnum, ServiceProviderStatusEnum, ServiceRequestStatusEnum } from '../../enums';
import { ServiceProviderDto, ServiceRequestDto } from '../../models';
import { serviceRequestsMock } from './mocks/service-requests.mock';

// NOTE fake api, replace with BaseApiService calls when backend endpoints are ready
@Injectable({ providedIn: 'root' })
export class RoadsideAssistanceApiService {
  private readonly requestDelay: number = 500;
  private serviceRequests: ServiceRequestDto[] = JSON.parse(JSON.stringify(serviceRequestsMock));

  getMany(params: GetManyV2Dto): Observable<PageableListV2Dto<ServiceRequestDto>> {
    const search: string = params.search?.trim().toLowerCase() || '';
    const statuses: string[] = params.statuses ? params.statuses.split(',') : [];
    const offset: number = params.offset || 0;
    const limit: number = params.limit || 25;

    const filtered: ServiceRequestDto[] = this.serviceRequests
      .filter((request: ServiceRequestDto) => !statuses.length || statuses.includes(request.status))
      .filter(
        (request: ServiceRequestDto) =>
          !search ||
          [request.number, request.vehicle.vehicleId, request.driver.fullName, request.issue.title].some(
            (value: string) => value.toLowerCase().includes(search),
          ),
      )
      .sort((a: ServiceRequestDto, b: ServiceRequestDto) => moment(b.createdAt).diff(moment(a.createdAt)));

    return of({
      data: this.clone(filtered.slice(offset, offset + limit)),
      offset,
      limit,
      total: filtered.length,
    }).pipe(delay(this.requestDelay));
  }

  getOne(id: string): Observable<ServiceRequestDto> {
    const request: ServiceRequestDto | undefined = this.findById(id);
    if (!request) {
      return throwError(() => new Error('Service request not found'));
    }

    return of(this.clone(request)).pipe(delay(this.requestDelay));
  }

  approveAndSend(id: string, approvedBy: string): Observable<ServiceRequestDto> {
    return this.update(id, (request: ServiceRequestDto) => {
      request.status = ServiceRequestStatusEnum.SENT;
      request.approvedBy = approvedBy;
      request.approvedAt = this.now();
      this.addTimelineEvent(request, `Request approved and sent by ${approvedBy}.`);
    });
  }

  // NOTE provider may be missing in the mock when it comes from an assistant report station, selection is still stored
  selectProvider(id: string, provider: ServiceProviderDto): Observable<ServiceRequestDto> {
    return this.update(id, (request: ServiceRequestDto) => {
      request.providers.forEach((p: ServiceProviderDto) => {
        if (p.status === ServiceProviderStatusEnum.SELECTED) {
          p.status = ServiceProviderStatusEnum.RESPONDED;
        }
        if (p.id === provider.id) {
          p.status = ServiceProviderStatusEnum.SELECTED;
        }
      });
      request.selectedProviderId = provider.id;
      request.status = ServiceRequestStatusEnum.PROVIDER_ASSIGNED;
      this.addTimelineEvent(request, `${provider.name} selected.`);
    });
  }

  updateAssistanceType(id: string, assistanceType: AssistanceTypeEnum): Observable<ServiceRequestDto> {
    return this.update(id, (request: ServiceRequestDto) => {
      request.assistanceType = assistanceType;
    });
  }

  private update(id: string, mutate: (request: ServiceRequestDto) => void): Observable<ServiceRequestDto> {
    const request: ServiceRequestDto | undefined = this.findById(id);
    if (!request) {
      return throwError(() => new Error('Service request not found'));
    }

    mutate(request);
    request.updatedAt = this.now();

    return of(this.clone(request)).pipe(delay(this.requestDelay));
  }

  private addTimelineEvent(request: ServiceRequestDto, text: string): void {
    request.timeline.push({
      id: `t-${request.timeline.length + 1}`,
      text,
      createdAt: this.now(),
    });
  }

  private findById(id: string): ServiceRequestDto | undefined {
    return this.serviceRequests.find((request: ServiceRequestDto) => request.id === id);
  }

  private now(): string {
    return moment().format('YYYY-MM-DDTHH:mm:ss');
  }

  private clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
  }
}
